'use client'

import cn from 'clsx'
import { useCallback, useImperativeHandle, useRef } from 'react'
import { Image } from '~/components/image'
import { mapRange } from '~/libs/maths'
import s from './frame-sequence.module.css'

export function FrameSequence({ frames = [], className, priority, ref }) {
  const framesRefs = useRef([])
  const currentFrameIndexRef = useRef(0)

  const frame = useCallback((index) => {
    const frames = framesRefs.current

    currentFrameIndexRef.current = index
    frames.forEach((node, i) => {
      if (i === index) {
        node.style.visibility = 'visible'
      } else {
        node.style.visibility = 'hidden'
      }
    })
  }, [])

  const setProgress = useCallback(
    (progress, start = 0, end = frames.length - 1) => {
      const index = Math.floor(mapRange(0, 1, progress, start, end))
      frame(index)
    },
    [frame, frames]
  )

  const next = useCallback(
    ({ loop = true } = {}) => {
      let index = currentFrameIndexRef.current + 1

      if (index > frames.length - 1) {
        index = loop?.start || 0
      }

      frame(index)
    },
    [frame, frames]
  )

  const nodeRef = useRef()

  useImperativeHandle(
    ref,
    () => ({
      frame,
      setProgress,
      getCurrentFrameIndex: () => currentFrameIndexRef.current,
      getNode: () => nodeRef.current,
      next,
    }),
    [frame, setProgress, next]
  )

  return (
    <div className={cn(s.sequence, className)} ref={nodeRef}>
      {frames.map((frame, i) => (
        <Image
          ref={(node) => {
            framesRefs.current[i] = node
          }}
          unoptimized
          src={frame}
          key={frame}
          alt=""
          loading={priority ? 'eager' : 'lazy'}
          style={{
            visibility: i === 0 ? 'visible' : 'hidden',
          }}
          priority={priority}
        />
      ))}
    </div>
  )
}
